import { useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";

import { ListenFn, RealTimeContext } from "@/context/realtime/context";
import {
    Channel,
    FeedRecordUpdate,
    FlatFeedRecord,
    GameId,
    GameMetadata,
    GamePlyData,
    GameReportMeta,
} from "@/gen/realtime";

// Note that fn is called for every message, not just the ones that match the channel. This is okay,
// because useOnNotify is only used in this file
const useOnNotify = (channel: Channel | undefined, fn: ListenFn) => {
    const context = useContext(RealTimeContext);

    const refFn = useRef(fn);

    useEffect(() => {
        refFn.current = fn;
    }, [fn]);

    if (!context) {
        throw new Error("useOnNotify must be used within a RealtimeProvider");
    }

    useEffect(() => {
        if (!channel) {
            return;
        }

        const id = context.addListener(channel, msg => {
            refFn.current(msg);
        });
        return () => {
            context.removeListener(id);
        };
    }, [context, context.addListener, context.removeListener, channel]);
};

export const useRealtimeGame = (gameId: GameId, languageCode: string) => {
    const [game, setGame] = useState<GameMetadata | null>(null);
    const [plyData, setPlyData] = useState<GamePlyData | null>(null);
    const [loadingGame, setLoadingGame] = useState(true);
    const gameReport = useGameReportDataInLanguage(gameId, languageCode);

    const gameMetadataChannel = useMemo(
        () => ({ t: "GameMetadataUpdated", c: gameId }) as const,
        [gameId]
    );
    useOnNotify(gameMetadataChannel, msg => {
        switch (msg.t) {
            case "GameMetadataUpdated":
                setGame(msg.c);
                setLoadingGame(false);
                break;
        }
    });

    const gamePlyDataChannel = useMemo(
        () => ({ t: "GamePliesUpdated", c: gameId }) as const,
        [gameId]
    );
    useOnNotify(gamePlyDataChannel, msg => {
        if (msg.t === "GamePliesUpdated") {
            setPlyData(msg.c);
        }
    });

    return { game, plyData, gameReport, loading: loadingGame };
};

function feedRecordReducer(records: FlatFeedRecord[], update: FeedRecordUpdate | { t: "Reset" }) {
    if (update.t === "Updated") {
        const newRecords = [...records];
        for (const updatedRecord of update.c) {
            const existingIndex = records.findIndex(fr => fr.id === updatedRecord.id);
            if (existingIndex !== -1) {
                newRecords.splice(existingIndex, 1, updatedRecord);
            } else {
                newRecords.push(updatedRecord);
            }
        }

        return newRecords;
    }

    if (update.t === "Removed") {
        return records.filter(fr => fr.id !== update.c);
    }

    if (update.t === "Reset") {
        return [];
    }

    return records;
}

export type NestedFeedRecord = Omit<FlatFeedRecord, "parentId"> & {
    children?: NestedFeedRecord[];
};

function sortFeedRecords(a: NestedFeedRecord, b: NestedFeedRecord) {
    if (a.ply && b.ply) {
        return a.ply.plyIndex - b.ply.plyIndex;
    }

    const timestampA = a.ply?.timestamp ?? a.timestamp;
    const timestampB = b.ply?.timestamp ?? b.timestamp;

    return timestampA.getTime() - timestampB.getTime();
}
export type GameReport = {
    gameReportMeta: GameReportMeta;
    feedRecords: NestedFeedRecord[];
    gameReportAvailability?: string[];
};
const useGameReportDataInLanguage = (
    gameId: GameId,
    preferredLanguageCode: string
): GameReport | null => {
    const [gameReportMeta, setGameReportMeta] = useState<GameReportMeta>();
    const [flatFeedRecords, handleFeedRecordUpdate] = useReducer(feedRecordReducer, []);

    // Cache to preserve object identity for unchanged parent feed records across updates
    const parentCacheRef = useRef(
        new Map<string, { nested: NestedFeedRecord; source: FlatFeedRecord; childSig: string }>()
    );

    const feedRecords = useMemo(() => {
        const childMap = new Map<string, NestedFeedRecord[]>();

        // Build child lists grouped by parentId
        for (const record of flatFeedRecords) {
            if (record.parentId) {
                if (!childMap.has(record.parentId)) childMap.set(record.parentId, []);
                childMap.get(record.parentId)!.push(record as NestedFeedRecord);
            }
        }

        // Sort children amongst themselves
        for (const [, children] of childMap) {
            children.sort(sortFeedRecords);
        }

        // Rebuild parent list, preserving identity when possible
        const newCache = new Map<
            string,
            { nested: NestedFeedRecord; source: FlatFeedRecord; childSig: string }
        >();
        const parents: NestedFeedRecord[] = [];

        for (const record of flatFeedRecords) {
            if (record.parentId) continue; // only top-level parents here

            const children = childMap.get(record.id) ?? [];
            const childSig = children
                .map(c => `${c.id}:${new Date(c.updatedAt).getTime()}`)
                .join("|");
            const prev = parentCacheRef.current.get(record.id);

            if (prev && prev.source === record && prev.childSig === childSig) {
                // Unchanged: reuse previous nested object reference
                newCache.set(record.id, prev);
                parents.push(prev.nested);
            } else {
                // Changed: create a new nested object
                const nested: NestedFeedRecord = { ...(record as NestedFeedRecord), children };
                const entry = { nested, source: record, childSig };
                newCache.set(record.id, entry);
                parents.push(nested);
            }
        }

        // Replace cache for next run
        parentCacheRef.current = newCache;

        // Sort parents by timestamp/ply index
        parents.sort(sortFeedRecords);
        return parents;
    }, [flatFeedRecords]);

    const gameReportDataChannel = useMemo(
        () =>
            ({
                t: "GameReportUpdated",
                c: {
                    gameId,
                    languageCode: preferredLanguageCode,
                },
            }) satisfies Channel,
        [gameId, preferredLanguageCode]
    );
    useOnNotify(gameReportDataChannel, msg => {
        if (msg.t === "GameReportUpdated") {
            const gameReportUpdate = msg.c;
            if (gameReportUpdate.t === "FeedRecordUpdate") {
                const feedRecordUpdate = gameReportUpdate.c;
                handleFeedRecordUpdate(feedRecordUpdate);
            } else if (gameReportUpdate.t === "GameReportMetaUpdated") {
                setGameReportMeta(gameReportUpdate.c);
            }
        }
    });

    return gameReportMeta ? { gameReportMeta, feedRecords } : null;
};
